import Autolinker from 'autolinker';
import DOMPurify from 'dompurify';

// Anchors open outside the recruiter app, and the opened page gets no handle
// back to it.
function openLinksInNewTab(node: Element): void {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * Prepares an HTML-authored message body for `dangerouslySetInnerHTML`.
 *
 * Linkify first, sanitize last, so DOMPurify vets the anchors Autolinker adds
 * as well as the markup that arrived in the body.
 *
 * Autolinker's own `sanitizeHtml` option is deliberately not used here: it
 * escapes every `<` and `>`, which renders an email body as literal tags. Only
 * bodies that are plain text by nature can take that option.
 *
 * DOMPurify keeps `style` attributes, and in its default configuration keeps
 * `<style>` elements too. A `<style>` that leads the body is the exception: the
 * parser hoists it into `<head>`, which sanitizing a fragment discards.
 */
export function sanitizeMessageHtml(html?: string | null): string {
  if (!html) {
    return '';
  }

  DOMPurify.addHook('afterSanitizeAttributes', openLinksInNewTab);

  try {
    return DOMPurify.sanitize(Autolinker.link(html));
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', openLinksInNewTab);
  }
}

/**
 * Prepares an untrusted message body that is plain text by nature for
 * `dangerouslySetInnerHTML`.
 *
 * These bodies carry applicant-supplied text, so painting one as markup is a
 * stored XSS sink. Allowing no tags leaves text nodes only, and nothing can
 * execute. The HTML context is deliberate: an outbound body arrives
 * entity-escaped by Rails' `strip_tags`, so a text child would show `&amp;`
 * literally, while an inbound reply's text/plain part skips that branch and
 * arrives raw. Both read correctly through innerHTML.
 *
 * Every policy here shares the module-level DOMPurify singleton, so the
 * `finally` in each hooked policy is what keeps the anchor hook from running
 * here.
 */
export function stripMessageMarkup(html?: string | null): string {
  if (!html) {
    return '';
  }

  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/**
 * Prepares a plain-text message body that should still show clickable links
 * (an SMS or WhatsApp message) for `dangerouslySetInnerHTML`.
 *
 * An inbound SMS body is whatever the applicant texted, so it is never markup.
 * Autolinker's `sanitizeHtml` escapes every `<` and `>` before linkifying, so a
 * payload reads as literal text instead of disappearing the way
 * `stripMessageMarkup` would drop it. Entities are left alone, so an outbound
 * body that Rails entity-escaped still decodes once through innerHTML.
 *
 * DOMPurify then runs as a second layer, allowing only the anchors Autolinker
 * adds. Those anchors carry `target="_blank" rel="noopener noreferrer"` by
 * default, so no hook is needed.
 */
export function linkifyMessageText(text?: string | null): string {
  if (!text) {
    return '';
  }

  return DOMPurify.sanitize(Autolinker.link(text, { sanitizeHtml: true }), {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
}

/**
 * Prepares a chat widget bot reply for `dangerouslySetInnerHTML`.
 *
 * The widget bot stores its replies as HTML (a portal link, a bolded quote),
 * so escaping them the way `linkifyMessageText` does shows the recruiter
 * literal tags. The bot also interpolates the applicant's own text into some
 * replies unescaped, so this is not `sanitizeMessageHtml` either: its default
 * DOMPurify policy would let that text plant an `<img>` or a `<form>` in a bot
 * bubble. Only the tags the bot writes survive.
 *
 * The caller decides a body is a bot reply from fields the server sets, never
 * from the body itself.
 */
export function sanitizeWidgetBotHtml(html?: string | null): string {
  if (!html) {
    return '';
  }

  DOMPurify.addHook('afterSanitizeAttributes', openLinksInNewTab);

  try {
    return DOMPurify.sanitize(Autolinker.link(html), {
      ALLOWED_TAGS: ['a', 'b', 'br'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', openLinksInNewTab);
  }
}

export default sanitizeMessageHtml;
